import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import {
  Button, Icon, Modal, Popover
} from 'antd';
import AvailableSignOnField from 'components/AvailableSignOnField';
import {
  parseAvailableSignOn, sharedLocalValue, toLocalValue
} from 'library/availableSignOn';

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

const scopeLine = (items) => {
  if (!items.length) {
    return '';
  }
  if (items.length > 1) {
    return `Applies to all ${items.length} selected assignments.`;
  }
  const { user, start_time: start } = items[0];
  const name = user && user.first_name ? `${user.last_name}, ${user.first_name}` : 'UNALLOCATED';
  return `${name} · ${moment(start).format('ddd DD MMM')}.`;
};

const SignOnPopover = ({
  items, onApply, onClose, presets, rect
}) => {
  const single = items.length === 1;
  const [initial] = useState(() => (single
    ? { mixed: false, value: toLocalValue(items[0].available_sign_on || null, items[0].start_time) }
    : sharedLocalValue(items)));
  const [value, setValue] = useState(initial.value);
  // R12: initialise from the stored value so invalid stored data disables Apply on mount.
  const [valid, setValid] = useState(() => !parseAvailableSignOn(initial.value, { requireHalfHour: true }).error);
  const [touched, setTouched] = useState(false);
  const panel = useRef(null);
  // R18: block re-entry while Modal.confirm is open (rc-trigger fires onVisibleChange on
  // every mousedown outside the popup, including clicks inside the confirm dialog).
  const confirming = useRef(false);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !panel.current) {
        return;
      }
      // Keep keyboard focus inside the popover.
      const nodes = panel.current.querySelectorAll(FOCUSABLE);
      if (!nodes.length) {
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }
      else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    // Scrolling the timeline moves the bar away from the anchor, so close (never saves).
    const onScroll = (event) => {
      if (panel.current && event.target instanceof Node && panel.current.contains(event.target)) {
        return;
      }
      onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [onClose]);

  const handleVisibleChange = (visible) => {
    if (visible) {
      return;
    }
    if (confirming.current) {
      return;
    }
    if (!touched) {
      onClose();
      return;
    }
    confirming.current = true;
    Modal.confirm({
      title: 'Discard changes?',
      okText: 'Discard',
      cancelText: 'Keep editing',
      onOk: () => {
        confirming.current = false;
        onClose();
      },
      onCancel: () => {
        confirming.current = false;
      }
    });
  };

  const handleChange = (next, isValid) => {
    setValue(next);
    setValid(isValid);
    setTouched(true);
  };

  const title = (
    <div className="bhp-sign-on-popover__title">
      <Icon type="clock-circle" />
      <span>Set OT availability</span>
      <Button size="small" type="link" icon="close" aria-label="Close" onClick={onClose} />
    </div>
  );

  const content = (
    <div ref={panel} className="bhp-sign-on-popover" role="dialog" aria-label="Set OT availability">
      <div className="bhp-sign-on-popover__scope">{scopeLine(items)}</div>
      <AvailableSignOnField
        autoFocus
        size="small"
        mixed={initial.mixed}
        value={initial.value}
        presets={presets}
        anchor={single ? items[0].start_time : null}
        getPopupContainer={() => panel.current}
        onChange={handleChange}
      />
      <div className="bhp-sign-on-popover__actions">
        {single && initial.value && (
          <Button className="bhp-sign-on-popover__clear" size="small" type="danger" onClick={() => onApply(null)}>
            Clear
          </Button>
        )}
        <Button size="small" onClick={onClose}>Cancel</Button>
        <Button
          size="small"
          type="primary"
          disabled={!valid || (initial.mixed && !touched)}
          onClick={() => onApply(value)}
        >
          {single ? 'Apply' : `Apply to ${items.length}`}
        </Button>
      </div>
    </div>
  );

  return (
    <Popover
      visible
      title={title}
      content={content}
      placement="bottomLeft"
      trigger="click"
      overlayClassName="bhp-sign-on-popover-overlay"
      overlayStyle={{ width: 320 }}
      onVisibleChange={handleVisibleChange}
    >
      <span
        className="bhp-sign-on-popover__anchor"
        style={{
          position: 'fixed',
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          pointerEvents: 'none'
        }}
      />
    </Popover>
  );
};

SignOnPopover.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  onApply: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  presets: PropTypes.shape({ ds: PropTypes.string, ns: PropTypes.string }).isRequired,
  rect: PropTypes.shape({
    top: PropTypes.number, left: PropTypes.number, width: PropTypes.number, height: PropTypes.number
  }).isRequired
};

export default SignOnPopover;
